import {
  Dispatch,
  ReactElement,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { AFTER_SIGN_IN_ACTION_TYPES } from 'services/afterSignIn';
import { FavoritesCount, Menu, New, Wrapper } from './style';
import { MarketsSearch } from 'components/BetsMenu/components/MarketsSearch';
import { GlobalIcon } from 'components/GlobalIcon';
import { DEFAULT_SELECTED_MENU } from 'components/MarketsList';
import { useOpenBetBuilder } from 'newhooks/sportsbook/useOpenBetBuilder';
import { getIsLoggedIn } from 'store/selectors/user-data';
import './index.less';

if (SpringConfigs.DIRECTION === 'rtl') {
  import('./rtl.less');
}

type Prop = {
  marketGroups: {
    [key: string]: {
      name: string;
      isNew: boolean;
    };
  };
  onChange: Function;
  activeMenu: string;
  favCount: number;
  hideAll?: boolean;
  isLive?: boolean;
  regionAlias?: string;
  game: {
    team1_name: string;
    team2_name: string;
    sportcast_id: number;
    id: number;
  };
  hasBoostedEvents?: boolean | null;
  isAllCollapsed?: boolean;
  setIsAllCollapsed: Dispatch<SetStateAction<boolean | undefined>>;
  sportId: number;
  setSearchValue: (value: string | null) => void;
  searchValue: string | null;
};

export const BetsMenu = ({
  hasBoostedEvents,
  marketGroups,
  hideAll,
  activeMenu = 'all',
  onChange,
  isLive,
  favCount,
  game,
  regionAlias,
  isAllCollapsed,
  setIsAllCollapsed,
  sportId,
  setSearchValue,
  searchValue
}: Prop): ReactElement => {
  const handleActiveMenuChange = (menu: string) => {
    onChange(menu);
  };

  const { t } = useTranslation();

  const isLoggedIn = useSelector(getIsLoggedIn);

  const menuItems = useMemo(
    () => Object.keys(marketGroups).filter(market => market !== 'undefined'),
    [marketGroups]
  );

  const betBuilderProps = {
    sportId,
    fixtureId: game.sportcast_id,
    gameTitle: `${game.team1_name} - ${game.team2_name}`,
    afterSignInActonName: `handleActiveMenuChange_${game.id}_${AFTER_SIGN_IN_ACTION_TYPES.TOGGLE_BET_BUILDER_MODAL}`
  };

  const { openBetBuilder } = useOpenBetBuilder(
    betBuilderProps,
    isMobile() ? null : () => handleActiveMenuChange(ENotRealSports.betBuilder)
  );

  useEffect(() => {
    if (!isLoggedIn && activeMenu === ENotRealSports.betBuilder) {
      handleActiveMenuChange(DEFAULT_SELECTED_MENU);
    }
  }, [isLoggedIn]);

  const BoostedOddsMenuItem = useCallback(() => {
    if (hasBoostedEvents) {
      return (
        <div
          className={cc([
            'betsMenu__menu-item',
            {
              'betsMenu__menu-item-active':
                activeMenu === ENotRealSports.boostedOdds
            }
          ])}
          onClick={() => handleActiveMenuChange(ENotRealSports.boostedOdds)}
        >
          <GlobalIcon
            lib="generic"
            name="boostedOdds"
            theme="default"
            size={EIconSize._14}
          />
          {t('sportsbook.boostedOdds')}
        </div>
      );
    } else {
      return null;
    }
  }, [hasBoostedEvents, activeMenu, handleActiveMenuChange]);

  return (
    <Wrapper
      className={cc([
        'betsMenu__wrapper',
        { 'betsMenu__wrapper--hiddenMarketGroups': searchValue !== null }
      ])}
    >
      <Menu
        className={isMobile() ? 'betsMenu__filter-mobile' : 'betsMenu__filter'}
      >
        <MarketsSearch setSearchValue={setSearchValue} />
        {!SpringConfigs.USER_PERSONALIZATION_ENABLED ? (
          <div
            className={cc([
              'betsMenu__menu-item',
              {
                'betsMenu__menu-item-active': activeMenu === 'favourites'
              }
            ])}
            onClick={() => handleActiveMenuChange('favourites')}
          >
            <GlobalIcon
              lib="generic"
              name="favorite"
              theme="default"
              size={EIconSize._16}
              className="betsMenu__favorites-icon"
            />
            {!!favCount && (
              <FavoritesCount className="fav__count">{favCount}</FavoritesCount>
            )}
          </div>
        ) : null}
        {SpringConfigs.BET_BUILDER_SWITCHER &&
        game &&
        game.sportcast_id &&
        !isLive ? (
          SpringConfigs.HIDE_BET_BUILDER && regionAlias === 'Cyprus' ? null : (
            <div
              className={cc([
                'betsMenu__menu-item',
                {
                  'betsMenu__menu-item-active':
                    activeMenu === ENotRealSports.betBuilder
                }
              ])}
              onClick={(e: SyntheticEvent) => openBetBuilder(e)}
            >
              {t('sportsbook.betBuilder')}
            </div>
          )
        ) : null}
        {!hideAll && (
          <div
            className={cc([
              'betsMenu__menu-item',
              {
                'betsMenu__menu-item-active': activeMenu === 'all'
              }
            ])}
            onClick={() => handleActiveMenuChange('all')}
          >
            {t('betslip.all')}
          </div>
        )}
        <BoostedOddsMenuItem />
        {menuItems.map((key: string) => (
          <div
            className={cc([
              'betsMenu__menu-item',
              {
                'betsMenu__menu-item-active': activeMenu === key
              }
            ])}
            key={key}
            onClick={() => handleActiveMenuChange(key)}
          >
            {marketGroups[key].name}
            {marketGroups[key].isNew && <New>{t('account.new')}</New>}
          </div>
        ))}
      </Menu>
      <div
        className="collapseToggleWrapper"
        onClick={() => setIsAllCollapsed(prev => !prev)}
      >
        <GlobalIcon
          className="expandAllButton"
          name={isAllCollapsed ? 'expandAll' : 'collapseAll'}
          lib="generic"
          theme="default"
          size={EIconSize._32}
          skeleton={{
            detachPassedClassName: true,
            shape: 'square'
          }}
        />
      </div>
    </Wrapper>
  );
};
